// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon } from '@loa/ui-components';
import type { ProviderAccessErrorResponse, ProviderAccessErrorCode } from '../../types/providerAccess';
import { ProviderAccessPageShell } from './ProviderAccessPageShell';
import { ProviderAccessStatusCard } from './ProviderAccessStatusCard';

type ProviderAccessErrorProps = Readonly<{
  error: ProviderAccessErrorResponse;
}>;

function formatDate(isoDate?: string | null): string {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
}

interface ErrorContent {
  title: string;
  message: string;
  iconName: string;
}

function getErrorContent(
  code: ProviderAccessErrorCode,
  rejectedAt?: string | null,
  lastSubmittedAt?: string | null,
): ErrorContent {
  switch (code) {
    case 'TOKEN_NOT_FOUND':
      return {
        title: 'Page Not Found',
        message: 'This access link is not valid. Please check the link in your email and try again.',
        iconName: 'search',
      };
    case 'TOKEN_EXPIRED':
      return {
        title: 'Link Expired',
        message: 'This access link has expired. Please contact the brokerage for assistance.',
        iconName: 'clock outline',
      };
    case 'TOKEN_EXHAUSTED':
    case 'SUBMISSION_LIMIT_REACHED': {
      const date = formatDate(lastSubmittedAt);
      const dateText = date ? ` on ${date}` : '';
      return {
        title: 'Data Already Submitted',
        message: `Data for this request was already submitted${dateText}. Please contact the brokerage for assistance.`,
        iconName: 'file alternate outline',
      };
    }
    case 'TOKEN_REVOKED':
      return {
        title: 'Link No Longer Valid',
        message: 'This access link has been revoked. Please contact the brokerage for assistance.',
        iconName: 'lock',
      };
    case 'LOA_ALREADY_REJECTED': {
      const date = formatDate(rejectedAt);
      const dateText = date ? ` on ${date}` : '';
      return {
        title: 'Letter of Authority Rejected',
        message: `This letter of authority was rejected${dateText}. Please contact the brokerage for assistance if you need to revoke the rejection.`,
        iconName: 'document favorite',
      };
    }
    case 'BROKERAGE_INACTIVE':
      return {
        title: 'Service Unavailable',
        message: 'This service is currently unavailable. Please try again later.',
        iconName: 'warning sign',
      };
    case 'RATE_LIMITED':
      return {
        title: 'Too Many Requests',
        message: 'You have made too many requests. Please wait a moment and try again.',
        iconName: 'timer',
      };
    default:
      return {
        title: 'Something Went Wrong',
        message: 'An unexpected error occurred. Please try again or contact the brokerage.',
        iconName: 'warning sign',
      };
  }
}

function ErrorIcon({ iconName }: Readonly<{ iconName: string }>) {
  return (
    <div className="bg-error-text rounded-full w-[100px] h-[100px] flex items-center justify-center">
      <AppIcon name={iconName} size={56} color="white" />
    </div>
  );
}

export function ProviderAccessError({ error }: ProviderAccessErrorProps) {
  const content = getErrorContent(error.code, error.rejectedAt, error.lastSubmittedAt);

  return (
    <ProviderAccessPageShell
      brokerageName={error.brokerageName}
      brokerageLogoUrl={error.brokerageLogoUrl}
    >
      <ProviderAccessStatusCard
        icon={<ErrorIcon iconName={content.iconName} />}
        title={content.title}
        message={content.message}
      />
    </ProviderAccessPageShell>
  );
}
